import "@/App.css";
import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import ComplianceFooter from "@/components/ComplianceFooter";
import CookieConsent from "@/components/CookieConsent";
import TermsGate from "@/components/TermsGate";

const ExchangeHome = lazy(() => import("@/pages/ExchangeHome"));
const TopicDetail = lazy(() => import("@/pages/TopicDetail"));
const ShareCard = lazy(() => import("@/pages/ShareCard"));
const IndexPage = lazy(() => import("@/pages/IndexPage"));
const Portal = lazy(() => import("@/pages/Portal"));
const Organization = lazy(() => import("@/pages/Organization"));
const Terms = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const AIEthics = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.AIEthics })));
const FAQ = lazy(() => import("@/pages/FAQ"));
const GetStarted = lazy(() => import("@/pages/GetStarted"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Developers = lazy(() => import("@/pages/Developers"));
const Account = lazy(() => import("@/pages/Account"));
const Status = lazy(() => import("@/pages/Status"));
const Markets = lazy(() => import("@/pages/Markets"));
const Vcie = lazy(() => import("@/pages/Vcie"));
const Topics = lazy(() => import("@/pages/Topics"));
const Rating = lazy(() => import("@/pages/Rating"));
const Sector = lazy(() => import("@/pages/Sector"));
const ApiLicense = lazy(() => import("@/pages/ApiLicense"));
const IndexLicensing = lazy(() => import("@/pages/IndexLicensing"));
const EmbiGov = lazy(() => import("@/pages/EmbiGov"));
const GovTopicView = lazy(() => import("@/pages/GovTopicView"));
const GovReports = lazy(() => import("@/pages/GovReports"));
const GovNotice = lazy(() => import("@/pages/GovNotice"));
const QuarterlyReport = lazy(() => import("@/pages/QuarterlyReport"));
const Reports = lazy(() => import("@/pages/Reports"));
const Login = lazy(() => import("@/pages/Login"));
const Admin = lazy(() => import("@/pages/Admin"));
const EmbedTicker = lazy(() => import("@/pages/EmbedTicker"));
const EmbedMovers = lazy(() => import("@/pages/EmbedMovers"));
const EmbedPulse = lazy(() => import("@/pages/EmbedPulse"));
const FamilySummary = lazy(() => import("@/pages/FamilySummary"));
const EmbedGallery = lazy(() => import("@/pages/EmbedGallery"));
const ForCommunities = lazy(() => import("@/pages/ForCommunities"));
const Associations = lazy(() => import("@/pages/Associations"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const PulseLab = lazy(() => import("@/pages/PulseLab"));
const WatchLab = lazy(() => import("@/pages/WatchLab"));
const ChainLab = lazy(() => import("@/pages/ChainLab"));
const SsoEntry = lazy(() => import("@/pages/SsoEntry"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24" data-testid="route-loading">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#26F195] border-t-transparent" />
    </div>
  );
}

function Shell() {
  // Synchronous (pre-render) check: a Google OAuth return lands at /#session_id=...
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return (
      <div className="App min-h-screen bg-[#061025] flex flex-col">
        <Suspense fallback={<RouteFallback />}>
          <AuthCallback />
        </Suspense>
      </div>
    );
  }
  return (
    <div className="App min-h-screen bg-[#061025] flex flex-col">
      <Header />
      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<ExchangeHome />} />
            <Route path="/topic/:id" element={<TopicDetail />} />
            <Route path="/index/:ticker" element={<IndexPage />} />
            <Route path="/signals" element={<Navigate to="/" replace />} />
            <Route path="/this-week" element={<FamilySummary />} />
            <Route path="/embed-gallery" element={<EmbedGallery />} />
            <Route path="/newsrooms" element={<ForCommunities />} />
            <Route path="/for-communities" element={<Navigate to="/newsrooms" replace />} />
            <Route path="/associations" element={<Associations />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/gov/topic/:id" element={<GovTopicView />} />
            <Route path="/gov/reports" element={<GovReports />} />
            <Route path="/gov/notice" element={<GovNotice />} />
            <Route path="/organization" element={<Organization />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/ai-ethics" element={<AIEthics />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/pricing" element={<ConsumerOnly><Pricing /></ConsumerOnly>} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/account" element={<Account />} />
            <Route path="/status" element={<Status />} />
            <Route path="/rating" element={<Rating />} />
            <Route path="/sector/:slug" element={<Sector />} />
            <Route path="/developer-license" element={<ApiLicense />} />
            <Route path="/license" element={<IndexLicensing />} />
            <Route path="/embi-gov" element={<EmbiGov />} />
            <Route path="/vcie" element={<Vcie />} />
            <Route path="/vcie-demo" element={<Vcie />} />
            <Route path="/quarterly" element={<QuarterlyReport />} />
            <Route path="/quarterly/:period" element={<QuarterlyReport />} />
            <Route path="/markets" element={<ConsumerOnly><Markets /></ConsumerOnly>} />
            <Route path="/topics" element={<Topics />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/login" element={<Login />} />
            <Route path="/accept" element={<AcceptInvite />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminGuard><Admin /></AdminGuard>} />
            <Route path="/ingest" element={<Navigate to="/admin" replace />} />
            <Route path="/lab/pulse" element={<PulseLab />} />
            <Route path="/lab/watch" element={<WatchLab />} />
            <Route path="/lab/chain" element={<ChainLab />} />
            <Route path="/enter" element={<SsoEntry />} />
          </Routes>
        </Suspense>
      </main>
      <ComplianceFooter />
      <CookieConsent />
      <TermsGate />
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0A0F1E", border: "1px solid #1b2942", color: "#f4f4f5", fontFamily: "Geist Mono", fontSize: "12px" } }} />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Only platform admins may mount /admin — otherwise a non-admin (e.g. a B2G viewer) would fire
// dozens of admin API calls that all 403. Redirect before the heavy Admin page ever mounts.
function AdminGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return <RouteFallback />;            // still checking
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to={user.is_gov ? "/portal" : "/"} replace />;
  return children;
}

// Consumer-only pages must never render for a B2G (is_gov) viewer — send them to their portal.
function ConsumerOnly({ children }) {
  const { user } = useAuth();
  if (user && user.is_gov) return <Navigate to="/portal" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/embed/ticker" element={<EmbedTicker />} />
            <Route path="/embed/movers" element={<EmbedMovers />} />
            <Route path="/embed/pulse" element={<EmbedPulse />} />
            <Route path="/card/:id" element={<ShareCard />} />
            <Route path="/*" element={<Shell />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
